import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Users, MapPin, QrCode, Copy, Check, Share2, Flame, Download } from "lucide-react";
import { getPublicHandle, shareUrlFor } from "@/lib/api";
import SEO from "@/components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicHandle() {
  const { handle } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const clean = (handle || "").replace(/^@/, "").toLowerCase();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!clean) return;
    getPublicHandle(clean)
      .then(setData)
      .catch(() => setError(true));
  }, [clean]);

  if (error) {
    return (
      <main className="pt-40 pb-32 bg-canvas min-h-[70vh]" data-testid="public-handle-notfound">
        <div className="container-x max-w-2xl">
          <div className="label-eyebrow">Founder profile</div>
          <h1 className="mt-4 font-display font-medium text-forest text-4xl md:text-5xl tracking-tightest leading-[0.95]">
            No founder holds <span className="italic font-serif">@{clean}.instant</span> — yet.
          </h1>
          <p className="mt-6 text-ink/70 max-w-md">
            Handles are reserved by Founder 100 members only. If this feels like your name, take a seat while there are still 100 available.
          </p>
          <div className="mt-8 flex gap-3">
            <Link to="/waitlist" className="btn-accent">
              Reserve a Founder seat <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/founders" className="btn-ghost">
              Open Founder Circle
            </Link>
          </div>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="pt-40 pb-32 bg-canvas min-h-[70vh]">
        <div className="container-x">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-32 bg-forest/10 rounded" />
            <div className="h-16 w-2/3 bg-forest/10 rounded" />
            <div className="h-4 w-1/3 bg-forest/10 rounded" />
          </div>
        </div>
      </main>
    );
  }

  const shareLink = `${window.location.origin}/founders/${data.handle}`;
  const copyLink = () => {
    navigator.clipboard?.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `@${data.handle}.instant on Instantxfer CashApp`,
          text: `Meet ${data.name || "a Founder 100 member"} — Seat ${String(data.seat).padStart(3, "0")} on Instantxfer CashApp.`,
          url: shareLink,
        });
      } catch (_) {
        // dismissed
      }
    } else {
      copyLink();
    }
  };

  const joined = data.joined_at ? new Date(data.joined_at) : null;
  const joinedStr = joined
    ? joined.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

  return (
    <main className="pt-32 md:pt-40 pb-24 md:pb-32 bg-canvas" data-testid="public-handle-page">
      <SEO
        slug="founders"
        title={`@${data.handle}.instant · Seat ${String(data.seat).padStart(3, "0")} — Instantxfer CashApp`}
        description={
          data.testimonial
            ? `"${data.testimonial}" — ${data.name || "A founder"}, Seat ${String(data.seat).padStart(3, "0")} of 100.`
            : `${data.name || "A founder"} · Seat ${String(data.seat).padStart(3, "0")} · Founder 100 · Instantxfer CashApp scan-to-pay handle.`
        }
      />
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* LEFT: identity card */}
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
            className="lg:col-span-5 relative rounded-[32px] bg-forest text-canvas p-8 md:p-10 overflow-hidden"
            data-testid="public-handle-card"
          >
            <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-lime/25 blur-3xl" />
            <div className="flex items-start justify-between">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-lime">
                Founder 100 · Verified
              </div>
              <div className="flex items-center gap-2">
                {data.has_streak && (
                  <span
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-lime text-ink text-[10px] tracking-[0.22em] font-bold uppercase"
                    data-testid="public-streak-badge"
                  >
                    <Flame className="w-3 h-3" strokeWidth={2} /> streak
                  </span>
                )}
                <div className="w-10 h-10 rounded-full bg-lime text-ink grid place-items-center">
                  <QrCode className="w-4 h-4" strokeWidth={1.8} />
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-lime text-ink grid place-items-center font-display font-medium text-xl">
                {data.initials}
              </div>
              <div>
                <div className="font-display text-3xl tracking-tightest text-canvas leading-none">
                  {data.name || "Anonymous Founder"}
                </div>
                <div className="mt-1 text-canvas/60 text-sm">
                  Seat {String(data.seat).padStart(3, "0")} of 100
                </div>
              </div>
            </div>

            <div
              className="mt-8 rounded-2xl bg-canvas text-forest px-5 py-4 font-mono text-lg tracking-tight break-all"
              data-testid="public-handle-string"
            >
              @{data.handle}.instant
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4">
              <MiniStat kpi={data.referrals_count} label="Referrals" icon={Users} />
              <MiniStat kpi={data.corridor || "—"} label="Corridor" icon={MapPin} />
              <MiniStat kpi={joinedStr.split(" ").slice(-1)[0]} label="Joined" />
            </div>

            <div className="mt-8 pt-6 border-t border-canvas/15">
              <div className="label-eyebrow !text-lime">Send to this founder</div>
              <p className="mt-2 text-sm text-canvas/70">
                In the Instantxfer CashApp app, scan their QR or type <span className="font-mono text-lime">@{data.handle}.instant</span> in the recipient field. Money lands in the payout method they&apos;ve selected.
              </p>
            </div>
          </motion.section>

          {/* RIGHT: manifesto + share */}
          <div className="lg:col-span-7 space-y-6">
            {data.testimonial && (
              <blockquote
                className="relative pl-6 border-l-4 border-lime"
                data-testid="public-testimonial"
              >
                <div className="font-serif italic text-forest text-2xl md:text-4xl leading-[1.15] tracking-tight max-w-2xl">
                  &ldquo;{data.testimonial}&rdquo;
                </div>
                <div className="mt-3 text-[10px] tracking-[0.24em] uppercase text-ink/50">
                  — {data.name || "A founder"}, Seat {String(data.seat).padStart(3, "0")}
                </div>
              </blockquote>
            )}

            <div>
              <div className="label-eyebrow">Public profile</div>
              <h1
                className="mt-4 font-display font-medium text-forest text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest"
                data-testid="public-handle-headline"
              >
                A payment identity, <span className="italic font-serif text-forest/80">not an account number.</span>
              </h1>
              <p className="mt-6 text-ink/70 max-w-xl leading-relaxed">
                Handles on Instantxfer CashApp are portable across bank, mobile money and QR. One name, everywhere your money needs to go — no IBANs, no branch codes, no small print.
              </p>
            </div>

            <div className="card-flat p-6 md:p-8" data-testid="public-handle-share">
              <div className="label-eyebrow">Share this profile</div>
              <div className="mt-4 flex flex-col md:flex-row gap-2">
                <div className="flex-1 rounded-xl border border-forest/15 bg-canvas px-4 py-3 font-mono text-sm text-forest truncate">
                  {shareLink}
                </div>
                <button className="btn-primary md:w-auto justify-center" onClick={copyLink} data-testid="public-copy-btn">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Copied" : "Copy"}
                </button>
                <button className="btn-ghost md:w-auto justify-center" onClick={nativeShare} data-testid="public-share-btn">
                  <Share2 className="w-4 h-4" /> Share
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Link to="/waitlist" className="btn-accent justify-center" data-testid="public-cta-waitlist">
                Take a Founder seat <ArrowUpRight className="w-4 h-4" />
              </Link>
              <button onClick={() => nav("/founders")} className="btn-ghost justify-center" data-testid="public-cta-founders">
                Are you a founder? Sign in
              </button>
            </div>

            {data.referral_code && (
              <a
                href={`${API}/api/founders/kit/${data.referral_code}.pdf`}
                download
                className="mt-2 flex items-center justify-between gap-3 rounded-2xl border border-forest/15 bg-forest/[0.03] hover:bg-forest/[0.06] transition-colors p-5"
                data-testid="public-founder-kit"
              >
                <div>
                  <div className="label-eyebrow">Founder Kit · PDF</div>
                  <div className="mt-1.5 font-display text-forest text-lg tracking-tight">
                    Download {data.name ? `${data.name.split(" ")[0]}'s` : "this"} personalised welcome page
                  </div>
                  <div className="mt-0.5 text-xs text-ink/55">
                    Seat, handle, scannable QR &amp; a thank-you note — one page, printable.
                  </div>
                </div>
                <div className="shrink-0 w-12 h-12 rounded-full bg-lime text-ink grid place-items-center">
                  <Download className="w-5 h-5" strokeWidth={2} />
                </div>
              </a>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

function MiniStat({ kpi, label, icon: Icon }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-canvas/60 text-[10px] tracking-[0.22em] uppercase">
        {Icon && <Icon className="w-3 h-3" strokeWidth={1.8} />}
        {label}
      </div>
      <div className="mt-2 font-display text-2xl text-canvas tracking-tightest">
        {kpi}
      </div>
    </div>
  );
}
